import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, CircularProgress, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { useMemo, useState } from 'react';
import idmlServiceInstance from '../../services/IdmlService';

const STATUS_FILTERS = [
    { key: 'needsReview', label: 'Needs review', color: 'warning' },
    { key: 'overflow', label: 'Overflow', color: 'error' },
    { key: 'fontFallback', label: 'Font fallback', color: 'info' },
];

/**
 * Segment table (source/target rows), design doc §7. Editing a target cell PATCHes the segment
 * immediately on blur and reports the updated segment up via onSegmentUpdate so the parent's
 * shared segment map stays in sync (also used by the page-spread view). Per-row "Re-fit" and a
 * bulk "Re-fit all" trigger IdmlCopyFitService explicitly (design doc §4/§6) -- the one automatic
 * fit already ran server-side right after translation landed, so this view never triggers it on
 * load, only on demand.
 */
const IdmlSegmentTable = ({
    bookId,
    segments,
    languageCode,
    readOnly = false,
    onSegmentUpdate,
    onSegmentsBulkRefit,
}) => {
    const [savingIds, setSavingIds] = useState(new Set());
    const [refittingIds, setRefittingIds] = useState(new Set());
    const [bulkRefitting, setBulkRefitting] = useState(false);
    const [drafts, setDrafts] = useState({}); // segmentId -> in-progress text, before blur commits it
    const [activeStatusFilters, setActiveStatusFilters] = useState(new Set()); // empty = show all rows
    const [searchText, setSearchText] = useState('');

    const toggleStatusFilter = (key) => {
        setActiveStatusFilters((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const filteredSegments = useMemo(() => {
        let result = segments;

        if (activeStatusFilters.size > 0) {
            result = result.filter((segment) => {
                const translation = segment.translations?.[languageCode];
                if (!translation) return false;
                return [...activeStatusFilters].some((key) => translation[key]);
            });
        }

        const query = searchText.trim().toLowerCase();
        if (query) {
            result = result.filter((segment) => {
                const sourceText = segment.sourceText || '';
                const targetText = segment.translations?.[languageCode]?.text || '';
                return sourceText.toLowerCase().includes(query) || targetText.toLowerCase().includes(query);
            });
        }

        return result;
    }, [segments, languageCode, activeStatusFilters, searchText]);

    const handleRefit = async (segmentId) => {
        setRefittingIds((prev) => new Set(prev).add(segmentId));
        try {
            const updated = await idmlServiceInstance.refitSegment(bookId, segmentId, languageCode);
            if (onSegmentUpdate) {
                onSegmentUpdate(segmentId, updated);
            }
        } catch (err) {
            console.error('Failed to refit segment:', err);
        } finally {
            setRefittingIds((prev) => {
                const next = new Set(prev);
                next.delete(segmentId);
                return next;
            });
        }
    };

    const handleRefitAll = async () => {
        setBulkRefitting(true);
        try {
            await idmlServiceInstance.refitBook(bookId, languageCode);
            if (onSegmentsBulkRefit) {
                await onSegmentsBulkRefit();
            }
        } catch (err) {
            console.error('Failed to bulk refit book:', err);
        } finally {
            setBulkRefitting(false);
        }
    };

    const handleTextChange = (segmentId, value) => {
        setDrafts((prev) => ({ ...prev, [segmentId]: value }));
    };

    const commitEdit = async (segment) => {
        const segmentId = segment.id;
        const draft = drafts[segmentId];
        const translation = segment.translations?.[languageCode];
        const currentText = translation?.text || '';
        if (draft === undefined || draft === currentText) {
            return;
        }

        setSavingIds((prev) => new Set(prev).add(segmentId));
        try {
            const updated = await idmlServiceInstance.patchSegment(bookId, segmentId, {
                languageCode,
                text: draft,
                saveType: 'MANUAL',
            });
            if (onSegmentUpdate) {
                onSegmentUpdate(segmentId, updated);
            }
        } catch (err) {
            console.error('Failed to save segment edit:', err);
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev);
                next.delete(segmentId);
                return next;
            });
            setDrafts((prev) => {
                const next = { ...prev };
                delete next[segmentId];
                return next;
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'order',
                header: '#',
                size: 50,
            },
            {
                id: 'source',
                header: 'Source',
                accessorFn: (row) => row.sourceText || '',
                Cell: ({ row }) => (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {row.original.sourceText}
                    </Typography>
                ),
            },
            {
                id: 'target',
                header: 'Target',
                accessorFn: (row) => row.translations?.[languageCode]?.text || '',
                Cell: ({ row }) => {
                    const segment = row.original;
                    const translation = segment.translations?.[languageCode];
                    const value = drafts[segment.id] !== undefined ? drafts[segment.id] : translation?.text || '';
                    return (
                        <TextField
                            multiline
                            fullWidth
                            variant="standard"
                            value={value}
                            disabled={readOnly}
                            onChange={(e) => handleTextChange(segment.id, e.target.value)}
                            onBlur={() => commitEdit(segment)}
                            InputProps={{ disableUnderline: readOnly }}
                        />
                    );
                },
            },
            {
                id: 'status',
                header: 'Status',
                size: 160,
                Cell: ({ row }) => {
                    const translation = row.original.translations?.[languageCode];
                    if (!translation) return null;
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {translation.needsReview && (
                                <Chip label="Needs review" size="small" color="warning" variant="outlined" />
                            )}
                            {translation.overflow && (
                                <Tooltip title="Estimated to overflow its frame">
                                    <Chip label="Overflow" size="small" color="error" variant="outlined" />
                                </Tooltip>
                            )}
                            {translation.fontFallback && (
                                <Tooltip
                                    title={translation.fontFallbackDetails || 'Font substituted for script coverage'}
                                >
                                    <Chip label="Font fallback" size="small" color="info" variant="outlined" />
                                </Tooltip>
                            )}
                            {savingIds.has(row.original.id) && (
                                <Chip label="Saving..." size="small" variant="outlined" />
                            )}
                        </Box>
                    );
                },
            },
            ...(readOnly
                ? []
                : [
                      {
                          id: 'refit',
                          header: 'Re-fit',
                          size: 70,
                          Cell: ({ row }) => {
                              const segmentId = row.original.id;
                              const isRefitting = refittingIds.has(segmentId);
                              return (
                                  <Tooltip title="Re-run copy-fit for this segment">
                                      <span>
                                          <IconButton
                                              size="small"
                                              disabled={isRefitting}
                                              onClick={() => handleRefit(segmentId)}
                                          >
                                              {isRefitting ? (
                                                  <CircularProgress size={16} />
                                              ) : (
                                                  <RefreshIcon fontSize="small" />
                                              )}
                                          </IconButton>
                                      </span>
                                  </Tooltip>
                              );
                          },
                      },
                  ]),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [languageCode, drafts, savingIds, readOnly, refittingIds],
    );

    return (
        <MaterialReactTable
            columns={columns}
            data={filteredSegments}
            getRowId={(row) => row.id}
            initialState={{ density: 'compact', pagination: { pageSize: 100, pageIndex: 0 } }}
            enablePagination
            enableSorting
            enableBottomToolbar
            enableTopToolbar
            enableStickyHeader
            enableGlobalFilter={false}
            enableColumnFilters={false}
            enableColumnActions={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            muiTableBodyCellProps={{ sx: { verticalAlign: 'top' } }}
            renderTopToolbarCustomActions={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {STATUS_FILTERS.map(({ key, label, color }) => (
                            <Chip
                                key={key}
                                label={label}
                                size="small"
                                color={color}
                                variant={activeStatusFilters.has(key) ? 'filled' : 'outlined'}
                                onClick={() => toggleStatusFilter(key)}
                            />
                        ))}
                        {!readOnly && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={bulkRefitting ? <CircularProgress size={16} /> : <RefreshIcon />}
                                disabled={bulkRefitting}
                                onClick={handleRefitAll}
                            >
                                Re-fit all
                            </Button>
                        )}
                    </Box>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search text..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />
                </Box>
            )}
        />
    );
};

export default IdmlSegmentTable;
